import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { personalInfo } from "../data/personalInfo";
import Button from "./Button";
import { socialLinks } from "../data/socialLinks";
import { ArrowRight, Sparkles, Terminal, FileCode, Cpu, Code2 } from "lucide-react";

function AnimatedRoles({ roles }) {
  const [displayedRole, setDisplayedRole] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(100);

  useEffect(() => {
    let timer;
    const handleType = () => {
      const i = loopNum % roles.length;
      const fullText = roles[i];
      
      setDisplayedRole(isDeleting 
        ? fullText.substring(0, displayedRole.length - 1)
        : fullText.substring(0, displayedRole.length + 1)
      );

      setTypingSpeed(isDeleting ? 30 : 90);

      if (!isDeleting && displayedRole === fullText) {
        setTimeout(() => setIsDeleting(true), 2200);
      } else if (isDeleting && displayedRole === '') {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };

    timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [displayedRole, isDeleting, loopNum, roles, typingSpeed]);

  return (
    <span className="relative inline-block min-h-[1.4em]">
      <span className="bg-gradient-to-r from-indigo-500 via-purple-400 to-pink-500 bg-clip-text text-transparent font-bold">
        {displayedRole}
      </span>
      <span className="border-r-2 border-indigo-500 dark:border-indigo-400 animate-pulse ml-1 inline-block h-[0.85em] align-middle"></span>
    </span>
  );
}

const Hero = () => {
  const particleRef = useRef(null);
  
  useEffect(() => {
    const canvas = particleRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    
    const particles = [];
    const particleCount = window.innerWidth < 768 ? 30 : 60;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        color: `rgba(99, 102, 241, ${Math.random() * 0.3 + 0.15})`,
        speedX: (Math.random() - 0.5) * 0.4,
        speedY: (Math.random() - 0.5) * 0.4
      });
    }
    
    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw connecting lines between particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(129, 140, 248, ${0.12 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      particles.forEach(particle => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
        
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
        if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    const handleResize = () => {
      resizeCanvas();
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const roles = [
    "Full-Stack Web Developer",
    "Mobile App Specialist",
    "React & Next.js Architect",
    "UI/UX Implementation Pro"
  ];



  return (
    <section
      id="home"
      className="min-h-screen flex items-center pt-28 sm:pt-36 pb-16 relative overflow-hidden bg-grid-pattern"
    >
      {/* Background canvas for connected node particles */}
      <canvas 
        ref={particleRef} 
        className="absolute inset-0 z-0 pointer-events-none opacity-70"
        style={{ willChange: 'transform' }}
      ></canvas>
      
      {/* Gradient background with mesh overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/60 via-white to-purple-50/40 dark:from-gray-950 dark:via-gray-950 dark:to-gray-900 z-0"></div>
      
      {/* Animated Glowing Orbs */}
      <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-gradient-to-r from-purple-500/20 to-indigo-500/20 blur-3xl animate-pulse-glow dark:from-purple-900/30 dark:to-indigo-900/30"></div>
      <div className="absolute bottom-10 left-10 w-80 h-80 rounded-full bg-gradient-to-r from-indigo-500/20 to-pink-500/20 blur-3xl animate-pulse-glow dark:from-indigo-900/30 dark:to-pink-900/30" style={{ animationDelay: '1.5s' }}></div>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-14">
          
          {/* Left Text Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="lg:w-7/12 text-left w-full"
          >
            {/* Availability Badge Pill */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="inline-flex items-center space-x-2 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-indigo-50/90 dark:bg-indigo-950/60 border border-indigo-200/80 dark:border-indigo-800/80 text-indigo-700 dark:text-indigo-300 text-xs sm:text-sm font-semibold mb-5 shadow-sm backdrop-blur-md max-w-full"
            >
              <span className="flex h-2.5 w-2.5 relative shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
              </span>
              <span className="truncate">Crafting high-impact web & mobile products</span>
              <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0 ml-0.5" />
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 tracking-tight font-heading leading-tight text-gray-900 dark:text-white">
              Hi, I'm{" "}
              <span className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
                {personalInfo.name}
              </span>
            </h1>
            
            <h2 className="text-lg sm:text-2xl lg:text-3xl mb-5 font-bold flex flex-wrap items-center gap-2 text-gray-800 dark:text-gray-200">
              <span>I specialize as a</span>
              <AnimatedRoles roles={roles} />
            </h2>
            
            <p className="text-sm sm:text-base lg:text-lg text-gray-600 dark:text-gray-300 mb-7 max-w-xl leading-relaxed pl-4 border-l-4 border-indigo-500 dark:border-indigo-400 rounded-sm">
              {personalInfo.bio}
            </p>

            {/* High-Contrast 3-Column Mini Stats Bar */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-md mb-8 py-3 px-3 sm:px-5 rounded-2xl bg-gray-100/90 dark:bg-gray-900/90 border border-gray-200/80 dark:border-gray-800 shadow-sm backdrop-blur-md">
              <div className="text-center border-r border-gray-200/80 dark:border-gray-800 pr-1 sm:pr-3">
                <span className="block text-base sm:text-xl font-extrabold text-indigo-600 dark:text-indigo-400 font-heading">5+ Yrs</span>
                <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Experience</span>
              </div>
              <div className="text-center border-r border-gray-200/80 dark:border-gray-800 px-1 sm:px-3">
                <span className="block text-base sm:text-xl font-extrabold text-purple-600 dark:text-purple-400 font-heading">20+</span>
                <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Apps Built</span>
              </div>
              <div className="text-center pl-1 sm:pl-3">
                <span className="block text-base sm:text-xl font-extrabold text-pink-600 dark:text-pink-400 font-heading">100%</span>
                <span className="block text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">Quality</span>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3.5 items-stretch sm:items-center max-w-md">
              <Button 
                variant="primary" 
                href="#projects"
                className="py-3.5 px-6 text-sm font-semibold shadow-lg shadow-indigo-500/25 flex-1"
              >
                <span>View Selected Work</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              
              <Button 
                variant="secondary" 
                href="#contact"
                className="py-3.5 px-6 text-sm font-semibold flex-1"
              >
                <span>Let's Build Together</span>
              </Button>
            </div>
            
            {/* Social Links */}
            <div className="mt-8 flex items-center space-x-3.5">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-widest mr-1">Follow Me:</span>
              {socialLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white dark:bg-gray-900 hover:bg-indigo-600 dark:hover:bg-indigo-600 flex items-center justify-center text-gray-700 dark:text-gray-300 hover:text-white dark:hover:text-white border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                  aria-label={link.name}
                >
                  <span className="text-base">{link.icon}</span>
                </a>
              ))}
            </div>
          </motion.div>
          
          {/* Right Interactive Code IDE Window */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:w-5/12 flex justify-center w-full mt-6 lg:mt-0"
          >
            <div className="relative group w-full max-w-lg">
              {/* Glowing Ambient Backdrop Aura */}
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl blur-2xl opacity-40 group-hover:opacity-60 transition-opacity duration-700 animate-pulse-glow"></div>

              {/* IDE Terminal Window Card */}
              <div className="relative bg-gray-950/95 dark:bg-gray-950/95 backdrop-blur-2xl rounded-2xl border border-gray-800 shadow-2xl overflow-hidden text-left font-mono">
                
                {/* macOS Title Bar */}
                <div className="px-4 py-3 bg-gray-900/90 border-b border-gray-800/80 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="w-3 h-3 rounded-full bg-rose-500 inline-block shadow-sm"></span>
                    <span className="w-3 h-3 rounded-full bg-amber-500 inline-block shadow-sm"></span>
                    <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block shadow-sm"></span>
                  </div>

                  <div className="flex items-center space-x-2 text-xs text-gray-400 font-sans font-semibold bg-gray-950/60 px-3 py-1 rounded-md border border-gray-800">
                    <FileCode className="w-3.5 h-3.5 text-indigo-400" />
                    <span>MatinKadri.config.ts</span>
                  </div>

                  <div className="flex items-center space-x-1.5">
                    <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500 animate-ping"></span>
                    <span className="text-[10px] font-sans text-emerald-400 font-bold uppercase tracking-wider">Active</span>
                  </div>
                </div>

                {/* Code Editor Content */}
                <div className="p-5 text-xs sm:text-sm leading-relaxed overflow-x-auto">
                  <div className="flex items-center text-gray-500 text-[11px] mb-3 font-sans space-x-2 border-b border-gray-900 pb-2">
                    <Terminal className="w-3.5 h-3.5 text-indigo-400" />
                    <span>{"// Developer Profile Specification"}</span>
                  </div>

                  <div className="space-y-1.5 font-mono">
                    <div>
                      <span className="text-purple-400">import</span>{" "}
                      <span className="text-yellow-300">{"{ Architect }"}</span>{" "}
                      <span className="text-purple-400">from</span>{" "}
                      <span className="text-emerald-300">'@matin/core'</span>;
                    </div>

                    <div className="pt-2">
                      <span className="text-purple-400">export const</span>{" "}
                      <span className="text-blue-400 font-bold">MatinKadri</span>:{" "}
                      <span className="text-yellow-300">Architect</span> = {"{"}
                    </div>

                    <div className="pl-4">
                      <span className="text-sky-300">role</span>:{" "}
                      <span className="text-emerald-300">'Full-Stack & Mobile Dev'</span>,
                    </div>

                    <div className="pl-4">
                      <span className="text-sky-300">mobile</span>: [
                      <span className="text-emerald-300">'React Native'</span>,{" "}
                      <span className="text-emerald-300">'Expo'</span>],
                    </div>

                    <div className="pl-4">
                      <span className="text-sky-300">web</span>: [
                      <span className="text-emerald-300">'React'</span>,{" "}
                      <span className="text-emerald-300">'Next.js'</span>,{" "}
                      <span className="text-emerald-300">'TypeScript'</span>],
                    </div>

                    <div className="pl-4">
                      <span className="text-sky-300">backend</span>: [
                      <span className="text-emerald-300">'Node.js'</span>,{" "}
                      <span className="text-emerald-300">'Supabase'</span>,{" "}
                      <span className="text-emerald-300">'Firebase'</span>],
                    </div>

                    <div className="pl-4">
                      <span className="text-sky-300">quality</span>:{" "}
                      <span className="text-emerald-300">'100% Production Ready'</span>
                    </div>

                    <div>{"};"}</div>
                  </div>

                  {/* Terminal Execution Status */}
                  <div className="mt-5 pt-3 border-t border-gray-900 flex items-center justify-between text-[11px] font-sans">
                    <div className="flex items-center text-emerald-400 space-x-1.5">
                      <Cpu className="w-3.5 h-3.5" />
                      <span className="font-semibold">Built & Type-Checked: 0 errors</span>
                    </div>
                    <span className="text-gray-500">v2.5.0</span>
                  </div>
                </div>

              </div>

              {/* Floating Pill Accent Badges */}
              <div className="absolute -top-3 -right-2 px-3 py-1.5 rounded-full bg-indigo-600 text-white font-sans text-[11px] font-bold shadow-lg flex items-center space-x-1.5 animate-float-slow">
                <Code2 className="w-3.5 h-3.5 text-amber-300" />
                <span>100% Type Safe</span>
              </div>

              <div className="absolute -bottom-3 -left-2 px-3 py-1.5 rounded-full bg-gray-900 border border-gray-800 text-gray-200 font-sans text-[11px] font-bold shadow-lg flex items-center space-x-1.5 animate-float-medium">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                <span>Mobile & Web Native</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Hero;